// "use client"

import React, { useState, useEffect, useRef } from "react"
import { Plus, FilePenLine, Trash, Filter, X, Eye, Check, XCircle } from "lucide-react"
import Modal from "../../components/Modal"
import { getTarifas, createTarifa, updateTarifa, deleteTarifa, getTiposTarifa } from "../../services/tarifasService"
import { getSucursalesDev } from "../../services/sucursalService"
import "./Tarifas.css"

/* ===== Helpers (sin cambios funcionales) ===== */
function buildRangos(tarifas) {
  const tarifasConRangos = []
  for (let i = 0; i < tarifas.length; i++) {
    const actual = tarifas[i]
    const siguiente = tarifas[i + 1]
    if (siguiente && siguiente.idSucursal === actual.idSucursal && siguiente.idTipoTarifa === actual.idTipoTarifa) {
      const limiteSuperior = siguiente.tiempo - 1
      tarifasConRangos.push({ ...actual, rangoTiempo: `${actual.tiempo} a ${limiteSuperior} min` })
    } else {
      tarifasConRangos.push({ ...actual, rangoTiempo: `${actual.tiempo} min en adelante` })
    }
  }
  return tarifasConRangos
}
const groupTarifasBySucursal = (tarifas) => {
  if (!tarifas.length) return []
  const grouped = tarifas.reduce((acc, tarifa) => {
    const sucursalNombre = tarifa.sucursal
    if (!sucursalNombre) return acc
    if (!acc[sucursalNombre]) {
      acc[sucursalNombre] = { sucursal: { id: tarifa.idSucursal, nombre: sucursalNombre }, tarifas: [] }
    }
    acc[sucursalNombre].tarifas.push(tarifa)
    return acc
  }, {})
  return Object.values(grouped)
}

const Tarifas = () => {
  const [tarifas, setTarifas] = useState([])
  const [tarifasAgrupadas, setTarifasAgrupadas] = useState([])
  const [sucursales, setSucursales] = useState([])
  const [tiposTarifa, setTiposTarifa] = useState([])
  const [formData, setFormData] = useState({ id: "", idSucursal: "", tiempo: "", montoTarifa: "", idTipoTarifa: 1 })

  const [editingTarifaId, setEditingTarifaId] = useState(null)
  const [inlineFormData, setInlineFormData] = useState({ id: "", tiempo: "", montoTarifa: "" })

  const [editing, setEditing] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedSucursal, setSelectedSucursal] = useState(null)
  const [tarifaToDelete, setTarifaToDelete] = useState(null)
  const [sucursalFilter, setSucursalFilter] = useState("")
  const [tipoTarifaFilter, setTipoTarifaFilter] = useState("")
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const filterInputRef = useRef(null)
  const [selectedTipoInModal, setSelectedTipoInModal] = useState("")

  const [isAddingTarifa, setIsAddingTarifa] = useState(false)
  const [newTarifaData, setNewTarifaData] = useState({ tiempo: "", montoTarifa: "" })
  const [lastTimeHint, setLastTimeHint] = useState("")

  const toggleFilter = () => setIsFilterOpen((p) => !p)
  const handleSucursalFilterChange = (e) => setSucursalFilter(e.target.value)
  const handleTipoTarifaFilterChange = (e) => setTipoTarifaFilter(e.target.value)
  const clearFilter = () => { setSucursalFilter(""); setTipoTarifaFilter("") }

  useEffect(() => { fetchAllTarifas(); fetchAllSucursales(); fetchTiposTarifa() }, [])
  useEffect(() => { if (isFilterOpen && filterInputRef.current) filterInputRef.current.focus() }, [isFilterOpen])
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterInputRef.current && !filterInputRef.current.contains(event.target) && !event.target.closest(".tar-column-header")) {
        setIsFilterOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const fetchAllTarifas = async () => {
    try {
      const data = await getTarifas()
      const sorted = data.sort((a, b) => {
        const sucA = a.sucursal || "", sucB = b.sucursal || ""
        if (sucA === sucB) {
          if (a.idTipoTarifa === b.idTipoTarifa) return a.tiempo - b.tiempo
          return a.idTipoTarifa - b.idTipoTarifa
        }
        return sucA.localeCompare(sucB)
      })
      const tarifasConRangos = buildRangos(sorted)
      setTarifas(tarifasConRangos)
      const groupedData = groupTarifasBySucursal(tarifasConRangos)
      setTarifasAgrupadas(groupedData)
      if (selectedSucursal) {
        const updated = groupedData.find((g) => g.sucursal.id === selectedSucursal.sucursal.id)
        updated ? setSelectedSucursal(updated) : closeDetailsModal()
      }
    } catch (err) { console.error("Error al obtener tarifas:", err.message) }
  }
  const fetchAllSucursales = async () => {
    try { setSucursales(await getSucursalesDev()) }
    catch (e) { console.error("Error al obtener sucursales:", e.message) }
  }
  const fetchTiposTarifa = async () => {
    try {
      const data = await getTiposTarifa()
      setTiposTarifa(data)
      if (data.length > 0) setFormData((p) => ({ ...p, idTipoTarifa: data[0].id }))
    } catch (e) {
      console.error("Error al obtener tipos de tarifa:", e.message)
      setTiposTarifa([{ id: 1, nombre: "Dia" }, { id: 2, nombre: "Noche" }, { id: 3, nombre: "Diario Dia" }, { id: 4, nombre: "Diario Noche" }])
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: name === "idSucursal" || name === "idTipoTarifa" ? Number.parseInt(value) : value }))
  }
  const handleNew = () => {
    const defaultTipoId = tiposTarifa.length > 0 ? tiposTarifa[0].id : 1
    setFormData({ id: "", idSucursal: "", tiempo: "", montoTarifa: "", idTipoTarifa: defaultTipoId })
    setEditing(false); setIsModalOpen(true)
  }
  const handleSubmit = async (e) => {
    e.preventDefault()
    const payload = {
      sucursal: { id: Number.parseInt(formData.idSucursal) },
      tiempo: Number.parseInt(formData.tiempo),
      montoTarifa: Number.parseFloat(formData.montoTarifa),
      tipoTarifa: { id: Number.parseInt(formData.idTipoTarifa) },
    }
    try {
      if (editing) await updateTarifa(formData.id, payload)
      else await createTarifa(payload)
      await fetchAllTarifas()
      alert(editing ? "Tarifa actualizada con éxito" : "Tarifa creada con éxito")
      closeModal()
    } catch (err) {
      console.error("Error al procesar la tarifa:", err.message)
      alert("Error al procesar la tarifa: " + err.message)
      closeModal()
    }
  }

  const handleDelete = async (id) => { setTarifaToDelete(id); setIsDeleteModalOpen(true) }
  const confirmDelete = async () => {
    try { await deleteTarifa(tarifaToDelete); await fetchAllTarifas(); alert("Tarifa eliminada con éxito") }
    catch (e) { console.error("Error al eliminar la tarifa:", e.message); alert("Ocurrió un error al eliminar la tarifa: " + e.message) }
    finally { closeDeleteModal() }
  }

  const handleInlineEdit = (tarifa) => {
    if (editingTarifaId) { alert("Por favor, guarde o cancele la edición actual antes de editar otra tarifa."); return }
    setEditingTarifaId(tarifa.id)
    setInlineFormData({ id: tarifa.id, tiempo: tarifa.tiempo, montoTarifa: tarifa.montoTarifa, idSucursal: tarifa.idSucursal, idTipoTarifa: tarifa.idTipoTarifa })
  }
  const cancelInlineEdit = () => setEditingTarifaId(null)
  const handleInlineFormChange = (e) => setInlineFormData((p) => ({ ...p, [e.target.name]: e.target.value }))
  const handleInlineFormSubmit = async (e) => {
    e.preventDefault()
    const payload = {
      sucursal: { id: Number.parseInt(inlineFormData.idSucursal) },
      tiempo: Number.parseInt(inlineFormData.tiempo),
      montoTarifa: Number.parseFloat(inlineFormData.montoTarifa),
      tipoTarifa: { id: Number.parseInt(inlineFormData.idTipoTarifa) },
    }
    try { await updateTarifa(inlineFormData.id, payload); setEditingTarifaId(null); await fetchAllTarifas() }
    catch (e) { console.error("Error al actualizar la tarifa:", e.message); alert("Error al actualizar la tarifa: " + e.message) }
  }

  const handleAddNewTarifaClick = () => {
    const tarifasParaMostrar = selectedSucursal.tarifas.filter((t) => t.tipoTarifa === selectedTipoInModal)
    if (tarifasParaMostrar.length > 0) {
      const ultima = tarifasParaMostrar[tarifasParaMostrar.length - 1]
      setLastTimeHint(`Último tiempo registrado: ${ultima.tiempo} min.`)
    } else setLastTimeHint("No hay tarifas previas. Se recomienda empezar en 0 min.")
    setIsAddingTarifa(true); setNewTarifaData({ tiempo: "", montoTarifa: "" })
  }
  const cancelAddNewTarifa = () => { setIsAddingTarifa(false); setLastTimeHint("") }
  const handleNewTarifaChange = (e) => setNewTarifaData((p) => ({ ...p, [e.target.name]: e.target.value }))

  const getTipoTarifaId = (nombre) => (tiposTarifa.find((t) => t.nombre === nombre)?.id ?? null)
  const handleNewTarifaSubmit = async (e) => {
    e.preventDefault()
    const tipoTarifaId = getTipoTarifaId(selectedTipoInModal)
    if (!selectedSucursal || !tipoTarifaId) { alert("No se pudo determinar la sucursal o el tipo de tarifa."); return }
    const payload = {
      sucursal: { id: Number.parseInt(selectedSucursal.sucursal.id) },
      tiempo: Number.parseInt(newTarifaData.tiempo),
      montoTarifa: Number.parseFloat(newTarifaData.montoTarifa),
      tipoTarifa: { id: Number.parseInt(tipoTarifaId) },
    }
    try { await createTarifa(payload); alert("Nueva tarifa creada con éxito"); setIsAddingTarifa(false); setLastTimeHint(""); await fetchAllTarifas() }
    catch (e) { console.error("Error al crear la tarifa:", e.message); alert("Error al crear la tarifa: " + e.message) }
  }

  const closeDeleteModal = () => { setIsDeleteModalOpen(false); setTarifaToDelete(null) }
  const closeModal = () => setIsModalOpen(false)
  const handleViewDetails = (sucursalData) => {
    setSelectedSucursal(sucursalData)
    const tiposDisponibles = [...new Set(sucursalData.tarifas.map((t) => t.tipoTarifa))]
    setSelectedTipoInModal(tiposDisponibles[0] || "Dia")
    setIsDetailsModalOpen(true)
  }
  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false); setSelectedSucursal(null); setSelectedTipoInModal(""); setEditingTarifaId(null)
    setIsAddingTarifa(false); setLastTimeHint("")
  }

  const filteredSucursales = tarifasAgrupadas.filter((group) => {
    if (sucursalFilter && !group.sucursal?.nombre?.toLowerCase().includes(sucursalFilter.toLowerCase())) return false
    if (tipoTarifaFilter) {
      const tieneTipo = group.tarifas.some((t) => t.tipoTarifa === tipoTarifaFilter)
      if (!tieneTipo) return false
    }
    return true
  })

  const renderColumnHeader = () => {
    const hasFilter = sucursalFilter && sucursalFilter.length > 0
    return (
      <th
        className={`tar-column-header tar-filterable ${isFilterOpen ? "tar-active-filter" : ""} ${hasFilter ? "tar-has-filter" : ""}`}
        onClick={toggleFilter}
      >
        <div className="tar-column-header-content">
          <span>Sucursal</span>
          <Filter size={14} className={`tar-filter-icon ${hasFilter ? "tar-filter-active" : ""}`} />
        </div>
        {isFilterOpen && (
          <div className="tar-column-filter-container" onClick={(e) => e.stopPropagation()}>
            <input
              ref={filterInputRef}
              type="text"
              className="tar-column-filter"
              placeholder="Filtrar por sucursal..."
              value={sucursalFilter}
              onChange={handleSucursalFilterChange}
            />
            {sucursalFilter && (
              <button className="tar-clear-filter-btn" onClick={clearFilter} aria-label="Limpiar filtro">
                <X size={14} />
              </button>
            )}
          </div>
        )}
      </th>
    )
  }

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 }).format(amount)

  const groupTarifasByTipo = (ts) => ts.reduce((acc, t) => { const tipo = t.tipoTarifa || "Dia"; (acc[tipo] ||= []).push(t); return acc }, {})
  const getTiposUnicos = () => { const tipos = new Set(); tarifas.forEach((t) => t.tipoTarifa && tipos.add(t.tipoTarifa)); return Array.from(tipos).sort() }

  return (
    <div className="tar-container">
      <div className="tar-header">
        <h1 className="tar-title">Tarifas Reglas de Negocio</h1>
        <div className="tar-header-decoration" />
      </div>

      <div className="tar-table-container">
        <div className="tar-table-header">
          <div className="tar-table-info">
            <span className="tar-table-count">Total: {filteredSucursales.length} sucursal{filteredSucursales.length !== 1 ? "es" : ""}</span>
            {sucursalFilter && (
              <div className="tar-active-filter-badge">
                Sucursal: {sucursalFilter}
                <button className="tar-clear-all-filters" onClick={() => setSucursalFilter("")}><X size={14} /></button>
              </div>
            )}
            {tipoTarifaFilter && (
              <div className="tar-active-filter-badge">
                Tipo: {tipoTarifaFilter}
                <button className="tar-clear-all-filters" onClick={() => setTipoTarifaFilter("")}><X size={14} /></button>
              </div>
            )}
            {(sucursalFilter || tipoTarifaFilter) && (
              <button onClick={clearFilter} className="tar-btn-primary tar-btn-sm" style={{ fontSize: "var(--font-size-xs)", padding: "4px 8px" }}>
                <X size={12} /> Limpiar filtros
              </button>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div className="tar-filter-container">
              <label>Tipo:</label>
              <select value={tipoTarifaFilter} onChange={handleTipoTarifaFilterChange} style={{ minWidth: 120 }}>
                <option value="">Todos los tipos</option>
                {getTiposUnicos().map((tipo) => (<option key={tipo} value={tipo}>{tipo}</option>))}
              </select>
            </div>
            <button onClick={handleNew} className="tar-btn-primary"><Plus size={16} /> Nueva Regla</button>
          </div>
        </div>

        <div className="tar-table-responsive">
          <table className="tar-table">
            <thead>
              <tr>
                <th className="tar-row-number-header">#</th>
                {renderColumnHeader()}
                <th>Reglas Definidas</th>
                <th className="tar-actions-header">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredSucursales.length > 0 ? (
                filteredSucursales.map((group, index) => (
                  <tr key={group.sucursal.nombre} className="tar-sucursal-group-row" onClick={() => handleViewDetails(group)}>
                    <td className="tar-row-number">{index + 1}</td>
                    <td className="tar-sucursal-cell">{group.sucursal.nombre}</td>
                    <td className="tar-tiempo-cell">
                      {(() => {
                        const tFiltradas = tipoTarifaFilter ? group.tarifas.filter((t) => t.tipoTarifa === tipoTarifaFilter) : group.tarifas
                        return `${tFiltradas.length} regla(s)`
                      })()}
                    </td>
                    <td>
                      <div className="tar-action-buttons">
                        <button className="tar-action-button tar-view-details-button" title="Ver detalles">
                          <Eye size={18} className="tar-view-icon" />
                          <span>Ver Detalles</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4">
                    <div className="tar-empty-message">
                      <div className="tar-empty-icon">📋</div>
                      <p>No hay tarifas registradas</p>
                      <button onClick={handleNew} className="tar-btn-primary tar-btn-sm"><Plus size={14} /> Crear nueva tarifa</button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===== Modal CREAR/EDITAR (aislado) ===== */}
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <div className="tar-modal-header">
          <h2 className="tar-modal-title">{editing ? "Actualizar Tarifa" : "Crear Tarifa"}</h2>
        </div>
        <div className="tar-modal-content">
          <form onSubmit={handleSubmit}>
            <div className="tar-modal-form-group">
              <label className="tar-modal-label">Sucursal:</label>
              <select name="idSucursal" value={formData.idSucursal} onChange={handleChange} required className="tar-modal-select">
                <option value="">-- Seleccione una sucursal --</option>
                {sucursales.map((suc) => (<option key={suc.id} value={suc.id}>{suc.nombre}</option>))}
              </select>
            </div>
            <div className="tar-modal-form-group">
              <label className="tar-modal-label">Tipo de Tarifa:</label>
              <select name="idTipoTarifa" value={formData.idTipoTarifa} onChange={handleChange} required className="tar-modal-select">
                {tiposTarifa.map((tipo) => (<option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>))}
              </select>
            </div>
            <div className="tar-modal-form-group">
              <label className="tar-modal-label">Tiempo (min):</label>
              <input type="number" name="tiempo" value={formData.tiempo} onChange={handleChange} required className="tar-modal-input" min="0" />
            </div>
            <div className="tar-modal-form-group">
              <label className="tar-modal-label">Monto:</label>
              <div className="tar-input-with-prefix">
                <span className="tar-input-prefix">$</span>
                <input type="number" step="0.01" name="montoTarifa" value={formData.montoTarifa} onChange={handleChange} required className="tar-modal-input tar-with-prefix" min="0" />
              </div>
            </div>
            <div className="tar-modal-actions">
              <button type="submit" className="tar-modal-btn tar-modal-btn-primary">{editing ? "Actualizar" : "Guardar"}</button>
              <button type="button" onClick={closeModal} className="tar-modal-btn tar-modal-btn-secondary">Cancelar</button>
            </div>
          </form>
        </div>
      </Modal>

      {/* ===== Modal DETALLES (aislado) ===== */}
      <Modal isOpen={isDetailsModalOpen} onClose={closeDetailsModal}>
        <div className="tar-modal-header"><h2 className="tar-modal-title">Tarifas de: {selectedSucursal?.sucursal.nombre}</h2></div>
        <div className="tar-details-modal-content">
          {selectedSucursal && (() => {
            const tarifasPorTipo = groupTarifasByTipo(selectedSucursal.tarifas)
            const tiposDisponibles = Object.keys(tarifasPorTipo)
            const tarifasParaMostrar = selectedSucursal.tarifas.filter((t) => t.tipoTarifa === selectedTipoInModal)
            return (
              <>
                <div className="tar-tipo-tarifa-tabs-container">
                  <div className="tar-tipo-tarifa-tabs">
                    {tiposDisponibles.map((tipo) => (
                      <button key={tipo} className={`tar-tipo-tab ${selectedTipoInModal === tipo ? "active" : ""}`}
                        onClick={() => { setSelectedTipoInModal(tipo); cancelInlineEdit(); cancelAddNewTarifa() }}>
                        {tipo}
                      </button>
                    ))}
                  </div>
                  <button onClick={handleAddNewTarifaClick} className="tar-btn-primary tar-btn-sm" disabled={editingTarifaId !== null || isAddingTarifa}>
                    <Plus size={14} /> Añadir Tarifa
                  </button>
                </div>

                {isAddingTarifa && (
                  <div className="tar-inline-editor-container tar-add-new-tarifa-container">
                    <form onSubmit={handleNewTarifaSubmit} className="tar-inline-editor-form">
                      <div className="tar-modal-form-group">
                        <label className="tar-modal-label">Tiempo (min)</label>
                        <div>
                          <input type="number" name="tiempo" value={newTarifaData.tiempo} onChange={handleNewTarifaChange} required className="tar-modal-input" min="0" autoFocus />
                          {lastTimeHint && <p className="tar-input-hint">{lastTimeHint}</p>}
                        </div>
                      </div>
                      <div className="tar-modal-form-group">
                        <label className="tar-modal-label">Monto</label>
                        <div className="tar-input-with-prefix">
                          <span className="tar-input-prefix">$</span>
                          <input type="number" step="0.01" name="montoTarifa" value={newTarifaData.montoTarifa} onChange={handleNewTarifaChange} required className="tar-modal-input tar-with-prefix" min="0" />
                        </div>
                      </div>
                      <div className="tar-inline-editor-actions">
                        <button type="submit" className="tar-action-button" title="Guardar Tarifa"><Check size={20} color="green" /></button>
                        <button type="button" className="tar-action-button" onClick={cancelAddNewTarifa} title="Cancelar"><XCircle size={20} color="red" /></button>
                      </div>
                    </form>
                  </div>
                )}

                <div>
                  <table className="tar-details-table">
                    <thead>
                      <tr>
                        <th>Rango Tiempo</th>
                        <th>Monto</th>
                        <th className="tar-actions-header">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tarifasParaMostrar.map((tarifa) => (
                        <React.Fragment key={tarifa.id}>
                          <tr>
                            <td>{tarifa.rangoTiempo}</td>
                            <td className="tar-monto-cell">{formatCurrency(tarifa.montoTarifa)}</td>
                            <td>
                              <div className="tar-action-buttons">
                                <button className="tar-action-button tar-edit-button" onClick={() => handleInlineEdit(tarifa)} title="Editar tarifa" disabled={(editingTarifaId && editingTarifaId !== tarifa.id) || isAddingTarifa}>
                                  <FilePenLine size={18} className="tar-edit-icon" />
                                </button>
                                <button className="tar-action-button tar-delete-button" onClick={() => handleDelete(tarifa.id)} title="Eliminar tarifa" disabled={!!editingTarifaId || isAddingTarifa}>
                                  <Trash size={18} className="tar-delete-icon" />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {editingTarifaId === tarifa.id && (
                            <tr className="tar-inline-editor-row">
                              <td colSpan="3">
                                <div className="tar-inline-editor-container">
                                  <form onSubmit={handleInlineFormSubmit} className="tar-inline-editor-form">
                                    <div className="tar-modal-form-group">
                                      <label className="tar-modal-label">Tiempo (min)</label>
                                      <input type="number" name="tiempo" value={inlineFormData.tiempo} onChange={handleInlineFormChange} required className="tar-modal-input" min="0" />
                                    </div>
                                    <div className="tar-modal-form-group">
                                      <label className="tar-modal-label">Monto</label>
                                      <div className="tar-input-with-prefix">
                                        <span className="tar-input-prefix">$</span>
                                        <input type="number" step="0.01" name="montoTarifa" value={inlineFormData.montoTarifa} onChange={handleInlineFormChange} required className="tar-modal-input tar-with-prefix" min="0" />
                                      </div>
                                    </div>
                                    <div className="tar-inline-editor-actions">
                                      <button type="submit" className="tar-action-button" title="Guardar Cambios"><Check size={20} color="green" /></button>
                                      <button type="button" className="tar-action-button" onClick={cancelInlineEdit} title="Cancelar Edición"><XCircle size={20} color="red" /></button>
                                    </div>
                                  </form>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  {tarifasParaMostrar.length === 0 && !isAddingTarifa && (
                    <div className="tar-no-tarifas-message">
                      <div className="tar-empty-icon">💸</div>
                      <p>No hay reglas de tarifa para este tipo. <br />¡Añade la primera!</p>
                    </div>
                  )}
                </div>
              </>
            )
          })()}
        </div>
        <div className="tar-modal-actions">
          <button type="button" onClick={closeDetailsModal} className="tar-modal-btn tar-modal-btn-secondary">Cerrar</button>
        </div>
      </Modal>

      {/* ===== Modal CONFIRMAR ELIMINACIÓN (aislado) ===== */}
      <Modal isOpen={isDeleteModalOpen} onClose={closeDeleteModal}>
        <div className="tar-modal-header"><h2 className="tar-modal-title">Confirmar Eliminación</h2></div>
        <div className="tar-modal-content">
          <div className="tar-delete-confirmation">
            <div className="tar-delete-icon-container"><Trash size={40} className="tar-delete-confirmation-icon" /></div>
            <p>¿Estás seguro de que deseas eliminar esta tarifa? Esta acción no se puede deshacer.</p>
          </div>
          <div className="tar-modal-actions">
            <button onClick={closeDeleteModal} className="tar-modal-btn tar-modal-btn-secondary">Cancelar</button>
            <button onClick={confirmDelete} className="tar-modal-btn tar-modal-btn-danger">Eliminar</button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default Tarifas
